import { useCallback, useMemo } from 'react'
import { FiCheck, FiLock, FiMenu } from 'react-icons/fi'

import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { t } from 'i18next'

import {
  DragHandle,
  HiddenCheckbox,
  Item,
  ItemContent,
  ItemLabel,
  ToggleIcon
} from './styles'

type SortableItemProps<T extends string> = {
  card: CardSelectorItem<T>
  isChecked: boolean
  isLocked: boolean
  onToggle: (type: T) => void
}

const SortableItem = <T extends string>({
  card,
  isChecked,
  isLocked,
  onToggle
}: SortableItemProps<T>) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: card.type })

  const handleToggleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.stopPropagation()
      onToggle(card.type)
    },
    [card.type, onToggle]
  )

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  }

  const toggleIcon = useMemo(() => {
    if (isLocked) return <FiLock />
    if (isChecked) return <FiCheck />

    return null
  }, [isChecked, isLocked])

  const inputId = `card-selector-toggle-${card.type}`

  return (
    <Item ref={setNodeRef} style={style} data-selected={isChecked}>
      <DragHandle
        type="button"
        aria-label={t('common:actions.edit')}
        data-dragging={isDragging}
        {...attributes}
        {...listeners}
      >
        <FiMenu />
      </DragHandle>

      <HiddenCheckbox
        id={inputId}
        checked={isChecked}
        disabled={isLocked}
        onChange={handleToggleChange}
      />

      <ItemContent htmlFor={inputId} data-locked={isLocked}>
        <ItemLabel>{card.label}</ItemLabel>

        <ToggleIcon data-selected={isChecked} data-locked={isLocked}>
          {toggleIcon}
        </ToggleIcon>
      </ItemContent>
    </Item>
  )
}

export default SortableItem
